import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { notification } from 'antd';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  EXECUTION_STATE,
  parseExecutionState,
  STALLABLE_EXECUTION_STATES,
} from '@src/models/execution';
import type { CommonReduxState } from '@src/models/commons/reduxState.interface';
import { ALLOWED_ROLES, useAllowedRoleChecker } from '@src/packages/utils';
import useGetExecutionStateTransitions from '@src/packages/utils/hooks/collections/useGetExecutionStateTransitions';
import { useExecutionStatesRt } from '@src/packages/utils/hooks/useExecutionStatesRt';
import ExecutionStateService from '@src/services/ExecutionStateService';
import { getUserById } from '@src/util/firebase-operations/users/get';
import { sendErrorToSentry } from '@src/util/sentry/utils';

import {
  buildDelayRows,
  getDelaysDurationSeconds,
  type StateTransitionRecord,
} from '../delayRows';
import { getUserLabelKey } from '../getUserLabelKey';

interface TaskDetailsRouteParams {
  divId: string;
  orderId: string;
}

interface UseDelaysOptions {
  divisionId?: string;
  executionId?: string;
  readOnly?: boolean;
}

interface UserNameData {
  firstName?: string;
  lastName?: string;
}

export const useDelays = ({
  divisionId,
  executionId,
  readOnly = false,
}: UseDelaysOptions = {}) => {
  const intl = useIntl();
  const { divId: routeDivisionId, orderId: routeExecutionId } =
    useParams<TaskDetailsRouteParams>();
  const divId = divisionId ?? routeDivisionId;
  const orderId = executionId ?? routeExecutionId;
  const orgId = useSelector(
    ({ organizations }: CommonReduxState) => organizations.organization.id,
  );

  const executionStateServiceRef = useRef<ExecutionStateService | null>(null);
  if (executionStateServiceRef.current === null) {
    executionStateServiceRef.current = new ExecutionStateService();
  }
  const executionStateService = executionStateServiceRef.current;

  const { getExecutionStateTransitions } = useGetExecutionStateTransitions();
  const [transitions, setTransitions] = useState<StateTransitionRecord[]>([]);
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const executionRefs = useMemo(
    () => (!readOnly && orderId && divId ? [{ id: orderId, divisionId: divId }] : []),
    [divId, orderId, readOnly],
  );
  const { statesByExecutionId } = useExecutionStatesRt(orgId, executionRefs);
  const currentState = (statesByExecutionId[orderId]?.currentState ?? '') as string;

  /**
   * `currentState` is a dependency on purpose: the backend trigger writes the transition
   * asynchronously after the stall document lands, and the state document is what says it
   * is done. Refetching on that signal keeps the table in sync without polling or an
   * arbitrary timeout.
   */
  useEffect(() => {
    let isCurrent = true;

    const fetchTransitions = async () => {
      if (!orgId || !divId || !orderId) {
        if (isCurrent) setTransitions([]);
        return;
      }

      setLoading(true);
      try {
        const result = await getExecutionStateTransitions(orgId, divId, orderId);
        if (isCurrent) setTransitions(result ?? []);
      } catch {
        if (isCurrent) setTransitions([]);
      } finally {
        if (isCurrent) setLoading(false);
      }
    };

    void fetchTransitions();

    return () => {
      isCurrent = false;
    };
  }, [currentState, divId, getExecutionStateTransitions, orderId, orgId]);

  const delayRows = useMemo(() => buildDelayRows(transitions), [transitions]);
  const userIds = useMemo(
    () =>
      Array.from(
        new Set(
          delayRows
            .map(delay => delay.userId)
            .filter((userId): userId is string => Boolean(userId) && !getUserLabelKey(userId)),
        ),
      ),
    [delayRows],
  );
  const totalDurationSeconds = useMemo(
    () => getDelaysDurationSeconds(delayRows),
    [delayRows],
  );

  useEffect(() => {
    let isCurrent = true;

    const fetchUserNames = async () => {
      if (!orgId || userIds.length === 0) {
        if (isCurrent) setUserNames({});
        return;
      }

      const names = await Promise.all(
        userIds.map(async userId => {
          try {
            const user = (await getUserById(orgId, userId)) as UserNameData | null;
            const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
            return [userId, fullName || userId] as const;
          } catch {
            return [userId, userId] as const;
          }
        }),
      );

      if (isCurrent) setUserNames(Object.fromEntries(names));
    };

    void fetchUserNames();

    return () => {
      isCurrent = false;
    };
  }, [orgId, userIds]);

  const canAccess = useAllowedRoleChecker();
  const canUpdate =
    !readOnly &&
    canAccess(ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.WORK_ORDER.UPDATE, divId);
  const isStalled = currentState === EXECUTION_STATE.stalled;
  const reportableState = parseExecutionState(currentState);
  const canReport =
    canUpdate && reportableState !== null && STALLABLE_EXECUTION_STATES.includes(reportableState);
  const canEnd = canUpdate && isStalled;

  const notifySuccess = useCallback(() => {
    notification.success({
      message: intl.formatMessage({ id: 'general.save.successful.message' }),
      placement: 'topRight',
    });
  }, [intl]);

  const notifyError = useCallback(
    (error: unknown, functionName: string) => {
      sendErrorToSentry(error, { functionName });
      notification.error({
        message: intl.formatMessage({ id: 'general.save.error.message' }),
        placement: 'topRight',
      });
    },
    [intl],
  );

  const reportDelay = useCallback(
    async (reason: string) => {
      try {
        setSubmitting(true);
        await executionStateService.reportStall(
          { currentState },
          orgId,
          divId,
          orderId,
          reason,
        );
        notifySuccess();
      } catch (error) {
        notifyError(error, 'useDelays.reportDelay');
      } finally {
        setSubmitting(false);
      }
    },
    [currentState, divId, executionStateService, notifyError, notifySuccess, orderId, orgId],
  );

  const endDelay = useCallback(async () => {
    try {
      setSubmitting(true);
      await executionStateService.endStall({ currentState }, orgId, divId, orderId);
      notifySuccess();
    } catch (error) {
      notifyError(error, 'useDelays.endDelay');
    } finally {
      setSubmitting(false);
    }
  }, [currentState, divId, executionStateService, notifyError, notifySuccess, orderId, orgId]);

  return {
    intl,
    delayRows,
    userNames,
    totalDurationSeconds,
    loading,
    isStalled,
    canReport,
    canEnd,
    submitting,
    reportDelay,
    endDelay,
  };
};

export default useDelays;
